/**
 * Service for handling concurrent match creation and edge cases
 */

import chatService from "@features/messaging/services/chatService";
import type { User } from "@fitties/shared";
import { auth } from "@infrastructure/config/firebase";
import { firebaseFunctionsService } from "@infrastructure/firebase/firebaseFunctionsService";
import { backendRegistry } from "@infrastructure/providers/backendRegistry";
import logger from "@logger";

import { fetchPublicUserProfiles } from "@shared/services/publicUserProfilesService";
import { getChatIdForPair } from "../utils/chatParticipants";

/**
 * Result of concurrent match handling
 */
export interface ConcurrentMatchResult {
  /** Whether a match was created */
  isMatch: boolean;
  /** Chat ID if match was created */
  chatId?: string;
  /** Error message if something went wrong */
  error?: string;
  /** Whether this was a duplicate request */
  isDuplicate: boolean;
}

/**
 * Service for handling concurrent match scenarios
 */
export class ConcurrentMatchService {
  /**
   * Handle concurrent likes between two users.
   * Re-invokes handleLike (idempotent) so mutual detection stays server-side.
   */
  async handleConcurrentLikes(userId1: string, userId2: string): Promise<ConcurrentMatchResult> {
    try {
      logger.info("Handling concurrent likes", { userId1, userId2 });

      const result = await this.processConcurrentMatch(userId1, userId2);

      logger.info("Concurrent likes processed", {
        userId1,
        userId2,
        isMatch: result.isMatch,
        chatId: result.chatId,
      });

      return result;
    } catch (error) {
      logger.error("Error handling concurrent likes:", error);
      return {
        isMatch: false,
        error: error instanceof Error ? error.message : "Unknown error",
        isDuplicate: false,
      };
    }
  }

  private async processConcurrentMatch(userId1: string, userId2: string): Promise<ConcurrentMatchResult> {
    const existingChat = await chatService.getChatForPair(userId1, userId2);
    if (existingChat?.id) {
      return {
        isMatch: true,
        chatId: existingChat.id,
        isDuplicate: true,
      };
    }

    const callerUid = auth.currentUser?.uid;
    const likedUserId = callerUid === userId1 ? userId2 : callerUid === userId2 ? userId1 : userId2;

    const likeResult = await firebaseFunctionsService.callFunctionWithData<
      { likedUserId: string },
      { mutual: boolean; chatId?: string }
    >("handleLike", { likedUserId });

    if (likeResult.data.mutual) {
      return {
        isMatch: true,
        chatId: likeResult.data.chatId ?? getChatIdForPair(userId1, userId2),
        isDuplicate: false,
      };
    }

    const chatAfter = await chatService.getChatForPair(userId1, userId2);
    if (chatAfter?.id) {
      return {
        isMatch: true,
        chatId: chatAfter.id,
        isDuplicate: true,
      };
    }

    return {
      isMatch: false,
      isDuplicate: false,
    };
  }

  /**
   * Find existing chat between two users (delegates to chat service).
   */
  async findExistingChat(userId1: string, userId2: string) {
    return chatService.getChatForPair(userId1, userId2);
  }

  /**
   * Handle rapid successive likes (spam prevention)
   */
  async handleRapidLikes(userId1: string, userId2: string, timestamp: number): Promise<ConcurrentMatchResult> {
    try {
      logger.info("Handling rapid likes", { userId1, userId2, timestamp });

      const now = Date.now();
      const timeDiff = now - timestamp;

      if (timeDiff < 1000) {
        logger.warn("Rapid successive like detected", { userId1, userId2, timeDiff });
        return {
          isMatch: false,
          isDuplicate: true,
          error: "Please wait before liking again",
        };
      }

      return await this.handleConcurrentLikes(userId1, userId2);
    } catch (error) {
      logger.error("Error handling rapid likes:", error);
      return {
        isMatch: false,
        error: error instanceof Error ? error.message : "Unknown error",
        isDuplicate: false,
      };
    }
  }

  /**
   * Handle network failures during match creation
   */
  async handleNetworkFailure(userId1: string, userId2: string): Promise<ConcurrentMatchResult> {
    try {
      logger.info("Handling network failure recovery", { userId1, userId2 });

      const existingChat = await this.findExistingChat(userId1, userId2);
      if (existingChat) {
        logger.info("Match was created despite network failure", {
          chatId: existingChat.id,
        });
        return {
          isMatch: true,
          chatId: existingChat.id,
          isDuplicate: false,
        };
      }

      return {
        isMatch: false,
        error: "Network failure prevented match creation",
        isDuplicate: false,
      };
    } catch (error) {
      logger.error("Error handling network failure:", error);
      return {
        isMatch: false,
        error: "Failed to recover from network failure",
        isDuplicate: false,
      };
    }
  }

  /**
   * Validate match creation request using public profiles + own blocked list.
   */
  async validateMatchRequest(userId1: string, userId2: string): Promise<{ isValid: boolean; error?: string }> {
    try {
      if (userId1 === userId2) {
        return { isValid: false, error: "Cannot match with yourself" };
      }

      const profiles = await fetchPublicUserProfiles([userId1, userId2]);
      const byUid = new Map(profiles.map((p) => [p.uid, p]));
      if (!byUid.has(userId1)) {
        return { isValid: false, error: "User 1 not found" };
      }
      if (!byUid.has(userId2)) {
        return { isValid: false, error: "User 2 not found" };
      }

      const viewer = await backendRegistry.database.getDocument<User>("users", userId1);
      if (viewer?.blockedUsers?.includes(userId2)) {
        return { isValid: false, error: "Users are blocked" };
      }

      return { isValid: true };
    } catch (error) {
      logger.error("Error validating match request:", error);
      return {
        isValid: false,
        error: error instanceof Error ? error.message : "Validation failed",
      };
    }
  }
}

export const concurrentMatchService = new ConcurrentMatchService();
export default concurrentMatchService;
